import {
    searchLocations,
    searchStopsById,
    getStopByRejseplanenId,
    searchStopsByLocation
} from "@/api/modules/locations";
import { filterChars } from "@/utilities/chars";

export const LOCATION_FROM = "FROM";
export const LOCATION_VIA = "VIA";
export const LOCATION_TO = "TO";
export const LOCATION_TYPES = [LOCATION_FROM, LOCATION_VIA, LOCATION_TO];

export const state = {
    locations: {
        [LOCATION_FROM]: null,
        [LOCATION_VIA]: null,
        [LOCATION_TO]: null
    },
    popularStops: []
};

export const mutations = {
    setLocationByType: (state, data) => {
        const stop = { ...data.value };
        if (data.type in state.locations) {
            state.locations[data.type] = stop;
        }
    },
    setPopularStops: (state, data) => {
        state.popularStops = data.stops;
    },
    swapLocations: state => {
        const prevFrom = state.locations[LOCATION_FROM];
        const prevTo = state.locations[LOCATION_TO];
        state.locations[LOCATION_FROM] = prevTo;
        state.locations[LOCATION_TO] = prevFrom;
    },
    unsetLocationFrom: state => {
        state.locations[LOCATION_FROM] = null;
    },
    unsetLocationVia: state => {
        state.locations[LOCATION_VIA] = null;
    },
    unsetLocationTo: state => {
        state.locations[LOCATION_TO] = null;
    },
    unsetLocations: state => {
        state.locations[LOCATION_FROM] = null;
        state.locations[LOCATION_TO] = null;
        state.locations[LOCATION_VIA] = null;
    }
};

export const getters = {
    getLocationByType: state => {
        return type => {
            return state.locations[type];
        };
    },
    getFromLocation: state => state.locations[LOCATION_FROM],
    getViaLocation: state => state.locations[LOCATION_VIA],
    getToLocation: state => state.locations[LOCATION_TO],
    getLocationRouteText: (state, getters) => {
        const from = getters.getFromLocation;
        const via = getters.getViaLocation;
        const to = getters.getToLocation;

        if (from === null || to === null) {
            return "";
        } else if (via === null) {
            return from.name + " - " + to.name;
        } else {
            return from.name + " - " + via.name + " - " + to.name;
        }
    },
    getPopularStops: state => {
        return state.popularStops;
    },
    hasDuplicates: (state, getters) => {
        const locationFrom = getters.getFromLocation;
        const locationVia = getters.getViaLocation;
        const locationTo = getters.getToLocation;

        if (locationFrom && locationTo && locationFrom.id === locationTo.id) {
            if (locationFrom.id == null && locationTo.id == null) {
                if (locationFrom.name === locationTo.name) {
                    return true;
                }
            } else {
                return true;
            }
        }

        if (locationFrom && locationVia && locationFrom.id === locationVia.id) {
            if (locationFrom.id == null && locationVia.id == null) {
                if (locationFrom.name === locationVia.name) {
                    return true;
                }
            } else {
                return true;
            }
        }

        if (locationTo && locationVia && locationTo.id === locationVia.id) {
            if (locationVia.id == null && locationTo.id == null) {
                if (locationVia.name === locationTo.name) {
                    return true;
                }
            } else {
                return true;
            }
        }

        return false;
    }
};

export const actions = {
    setLocationByType: (context, payload) => {
        // Update location
        context.commit("setLocationByType", payload);

        return Promise.resolve();
    },
    setLocationByTypeAndId: (context, { type, stopId }) => {
        return searchStopsById([stopId])
            .then(response => {
                if (!response.ok) throw new Error(JSON.stringify(response));
                return response.responseBody.stops[0];
            })
            .then(stop => {
                if (stop) {
                    return context.commit("setLocationByType", {
                        type,
                        value: stop
                    });
                } else {
                    return null;
                }
            });
    },
    setLocationByTypeAndGeo: (context, { type, longitude, latitude }) => {
        const productTypes = context.rootGetters["system/getStopProductTypes"];
        return searchStopsByLocation(longitude, latitude, productTypes).then(
            res => {
                if (!res.ok || !res.responseBody || !res.responseBody.stop)
                    return null;

                return context.dispatch("setLocationByTypeAndId", {
                    type,
                    stopId: res.responseBody.stop.id
                });
            }
        );
    },
    setStopsByRejseplanenIds: (context, { stopFromId, stopToId }) => {
        const productTypes = context.rootGetters["system/getStopProductTypes"];
        return Promise.all([
            getStopByRejseplanenId(stopFromId, productTypes),
            getStopByRejseplanenId(stopToId, productTypes)
        ]).then(([fromStop, toStop]) => {
            if (!fromStop.ok || !toStop.ok) return false;
            return Promise.all([
                context.dispatch("setLocationByTypeAndId", {
                    type: LOCATION_FROM,
                    stopId: fromStop.responseBody.id
                }),
                context.dispatch("setLocationByTypeAndId", {
                    type: LOCATION_TO,
                    stopId: toStop.responseBody.id
                })
            ]);
        });
    },
    searchPopularStops: context => {
        return context
            .dispatch("searchLocations", {
                search: ""
            })
            .then(stops => {
                context.commit("setPopularStops", {
                    stops: stops
                });
            });
    },
    searchLocations: (context, payload) => {
        const stopProductTypes =
            context.rootGetters["system/getStopProductTypes"];
        const validChars = context.rootGetters["system/commonCharacterSet"];

        return searchLocations(
            filterChars(payload.search, validChars),
            stopProductTypes,
            true
        )
            .then(response => {
                if (response.ok) {
                    return response.responseBody;
                }

                return Promise.reject(new Error(JSON.stringify(response)));
            })
            .then(searchResults => {
                return searchResults.locations;
            });
    }
};
